import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Brain, Shield, Zap, Clock, FileCheck, Layout, Sparkles, Check, Users, Star, ArrowRight } from 'lucide-react';

export function LandingPage() {
  return (
    <div className="min-h-screen">
      <Header isSidebarOpen={false} onSidebarToggle={() => {}} />

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              AI-Powered Clinical Documentation
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-6xl">
              Clinical Documentation for
              <span className="block text-blue-600 dark:text-blue-400">Mental Health Professionals</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-gray-600 dark:text-gray-300">
              Transform your therapy session notes into comprehensive, professional clinical documentation in minutes.
              Save time on paperwork and focus on what matters most—your clients.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/auth"
                className="rounded-lg bg-blue-600 px-8 py-4 text-lg font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:hover:bg-blue-800 transition-all hover:scale-105 flex items-center gap-2"
              >
                <span>Start Free Trial</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="#features"
                className="rounded-lg border-2 border-gray-300 dark:border-gray-600 px-8 py-4 text-lg font-semibold text-gray-900 dark:text-white hover:border-blue-600 dark:hover:border-blue-400 transition-colors"
              >
                Learn More
              </a>
            </div>
            <p className="mt-6 text-sm text-gray-600 dark:text-gray-400">
              5-day free trial • No credit card required
            </p>

            {/* Trust indicators */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                <span>Secure & Private</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                <span>Trusted by Therapists</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                <span>Easy to Use</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="py-24 bg-white dark:bg-gray-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl">
              Everything You Need for Clinical Documentation
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Sano combines AI technology with clinical expertise to help therapists, counselors, psychologists,
              and social workers create professional documentation efficiently.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-3">
            <Feature
              icon={Zap}
              title="AI-Powered Note Writing"
              description="Transform your raw session notes into polished SOAP, DAP, BIRP, or GIRP notes instantly. Our AI understands therapeutic language and maintains your professional voice."
            />
            <Feature
              icon={Clock}
              title="Save Hours Every Week"
              description="Cut documentation time by up to 50%. Spend less time on paperwork and more time with clients. Most therapists complete notes in 5-10 minutes."
            />
            <Feature
              icon={Brain}
              title="Multiple Note Formats"
              description="Switch between SOAP, DAP, BIRP, GIRP, and Progress Note formats effortlessly. Each format follows clinical standards and best practices."
            />
            <Feature
              icon={Shield}
              title="Privacy-First & Secure"
              description="Built with security best practices. We recommend de-identifying notes (no PII) to maintain HIPAA compliance."
            />
            <Feature
              icon={Layout}
              title="Guided Templates"
              description="Never miss important details with our structured templates and guided questions. Ensure thorough, consistent documentation every session."
            />
            <Feature
              icon={FileCheck}
              title="Client Management"
              description="Organize clients, track session history, and maintain comprehensive records. Perfect for solo practitioners and group practices alike."
            />
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-24 bg-gray-50 dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl">
              Simple, Efficient Workflow
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
              Create professional clinical documentation in three easy steps
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <Step
              number="1"
              title="Write Your Session Notes"
              description="Jot down your observations using free-form writing or our guided question prompts. Include interventions, client responses, and clinical observations."
            />
            <Step
              number="2"
              title="AI Processes Your Notes"
              description="Our intelligent system transforms your raw notes into structured, professional clinical documentation following your chosen format (SOAP, DAP, BIRP, etc.)."
            />
            <Step
              number="3"
              title="Review, Edit & Save"
              description="Review the AI-generated note, make any necessary edits, and save to your client's record. Export or copy for your EHR system."
            />
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-24 bg-white dark:bg-gray-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl mb-6">
                Built for Mental Health Professionals
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                Whether you're a therapist, psychologist, counselor, social worker, or psychiatrist,
                Sano adapts to your workflow and documentation needs.
              </p>
              <div className="space-y-4">
                <Benefit text="Supports CBT, DBT, Psychodynamic, Solution-Focused, and more therapeutic modalities" />
                <Benefit text="Customizable templates for different session types and client needs" />
                <Benefit text="Track client progress, concerns, interventions, and treatment plans" />
                <Benefit text="Works for individual therapy, couples counseling, group therapy, and family therapy" />
                <Benefit text="Perfect for private practice, group practices, and community mental health centers" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900/20 dark:to-blue-800/20 rounded-2xl p-8 border border-blue-200 dark:border-blue-800">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Who Uses Sano?
              </h3>
              <ul className="space-y-4 text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Licensed Therapists & Counselors</strong> streamlining session documentation</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Clinical Psychologists</strong> managing multiple clients and assessments</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Social Workers</strong> documenting case management and therapy sessions</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Group Practices</strong> standardizing documentation across team members</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Psychiatric Nurse Practitioners</strong> balancing medication management and therapy notes</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 bg-blue-600 dark:bg-blue-700">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white sm:text-4xl mb-6">
            Ready to Transform Your Documentation?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join mental health professionals who are saving time and improving their clinical documentation workflow with Sano.
          </p>
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 rounded-lg bg-white px-8 py-4 text-lg font-semibold text-blue-600 shadow-sm hover:bg-gray-100 transition-all hover:scale-105"
          >
            <span>Start Your Free 5-Day Trial</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-6 text-sm text-blue-100">
            No credit card required • Full access to all features • Cancel anytime
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 dark:bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <p className="text-gray-400 text-sm mb-4">
              &copy; {new Date().getFullYear()} Sano. All rights reserved.
            </p>
            <div className="flex items-center justify-center gap-6 text-sm">
              <Link
                to="/terms"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Terms of Service
              </Link>
              <span className="text-gray-600">•</span>
              <Link
                to="/privacy"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Privacy Policy
              </Link>
              <span className="text-gray-600">•</span>
              <a
                href="mailto:chase@sanocda.com"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon: Icon, title, description }: { icon: any; title: string; description: string }) {
  return (
    <div className="relative p-6 bg-gray-50 dark:bg-gray-800 rounded-xl hover:shadow-lg transition-all hover:-translate-y-1">
      <div className="absolute top-6 left-6">
        <span className="inline-flex items-center justify-center rounded-lg bg-blue-600/10 p-2 mb-2">
          <Icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </span>
      </div>
      <div className="mt-16 space-y-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function Step({ number, title, description }: { number: string; title: string; description: string }) {
  return (
    <div className="text-center">
      <div className="flex items-center justify-center w-16 h-16 mx-auto bg-blue-600 text-white text-2xl font-bold rounded-full shadow-lg mb-6">
        {number}
      </div>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
      <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
        {description}
      </p>
    </div>
  );
}

function Benefit({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-3">
      <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
      <span className="text-gray-700 dark:text-gray-300">{text}</span>
    </div>
  );
}
